import { useCallback, useMemo } from 'react';
import { useAppDispatch, useAppSelectorShallow } from 'store/hooks';
import { themeSelector } from 'store/reducer/setting/selectors';
import { updateThemeMode } from 'store/reducer/setting/slice';
import { THEME_OPTION, THEME_TOGGLE_VARIANT } from 'enums';
import Select, { SelectOption } from './Select';
import BaseButton from './BaseButton';
import Moon from 'assets/icons/Moon';
import SunSeason from 'assets/icons/SunSeason';
import { t } from 'services/i18n.service';

interface ThemeToggleProps {
  variant?: THEME_TOGGLE_VARIANT;
  size?: 'xs' | 'sm' | 'md' | 'lg';
  className?: string;
}

const ThemeToggle = ({
  variant = THEME_TOGGLE_VARIANT.FULL,
  size = 'sm',
  className
}: ThemeToggleProps) => {
  const dispatch = useAppDispatch();
  const currentTheme = useAppSelectorShallow(themeSelector);

  const themeOptions: SelectOption[] = useMemo(
    () => [
      {
        label: t('theme_toggle.light_mode'),
        value: THEME_OPTION.LIGHT
      },
      {
        label: t('theme_toggle.dark_mode'),
        value: THEME_OPTION.DARK
      }
    ],
    []
  );

  const handleThemeChange = useCallback(
    (value: string) => {
      dispatch(updateThemeMode(value as THEME_OPTION));
    },
    [dispatch]
  );

  const isDarkMode = useMemo(
    () => currentTheme === THEME_OPTION.DARK,
    [currentTheme]
  );

  const toggleTheme = useCallback(() => {
    const newTheme =
      currentTheme === THEME_OPTION.LIGHT
        ? THEME_OPTION.DARK
        : THEME_OPTION.LIGHT;
    dispatch(updateThemeMode(newTheme));
  }, [currentTheme, dispatch]);

  if (variant === THEME_TOGGLE_VARIANT.ICON_ONLY) {
    return (
      <BaseButton
        variant='icon'
        size={size}
        onClick={toggleTheme}
        aria-label={t(
          isDarkMode
            ? 'theme_toggle.switch_to_light_mode'
            : 'theme_toggle.switch_to_dark_mode'
        )}
        className={className}
        icon={
          isDarkMode ? (
            <SunSeason className='~w-4/5 ~h-4/5' />
          ) : (
            <Moon className='~w-4/5 ~h-4/5' />
          )
        }
      />
    );
  }

  return (
    <Select
      options={themeOptions}
      value={currentTheme}
      onChange={handleThemeChange}
      size={size}
      variant='outlined'
      className={className}
      isFullWidth={false}
      minWidth='8rem'
    />
  );
};

export default ThemeToggle;
